import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

interface NotionSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  /** 'flat': senza bordo né sfondo, da usare dentro una riga proprietà. */
  variant?: 'box' | 'flat';
}

export const NotionSelect = forwardRef<HTMLSelectElement, NotionSelectProps>(
  ({ label, error, hint, options, placeholder, variant = 'box', className = '', ...props }, ref) => {
    if (variant === 'flat') {
      return (
        <div className="relative inline-flex max-w-full items-center">
          <select
            ref={ref}
            className={`appearance-none max-w-full truncate rounded bg-transparent py-0.5 pl-1.5 pr-5 text-[13px] text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] focus:outline-none focus:bg-[rgba(55,53,47,0.08)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${className}`}
            {...props}
          >
            {placeholder && <option value="" disabled={props.value !== ''}>{placeholder}</option>}
            {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-1 h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
        </div>
      );
    }
    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-sm font-medium text-[#37352F] dark:text-white">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full px-3 py-2 pr-10
              bg-white dark:bg-gray-800
              border border-[rgba(55,53,47,0.16)] dark:border-gray-700
              rounded-md
              text-[#37352F] dark:text-white
              focus:outline-none focus:border-[#2383E2] focus:ring-2 focus:ring-[rgba(35,131,226,0.2)]
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors
              appearance-none
              ${error ? 'border-red-500 dark:border-red-500' : ''}
              ${className}
            `}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#787774] dark:text-gray-400 pointer-events-none" />
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {hint && !error && (
          <p className="text-sm text-[#787774] dark:text-gray-400">{hint}</p>
        )}
      </div>
    );
  }
);

NotionSelect.displayName = 'NotionSelect';

export default NotionSelect;
